import { useEffect, useState } from "react";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ComboSelect from "@/components/ComboSelect";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast, Toaster } from "sonner";
import { Link, ArrowsClockwise, GearSix, CheckCircle } from "@phosphor-icons/react";

export default function Advances() {
    const [items, setItems] = useState([]);
    const [indicative, setIndicative] = useState([]);
    const [showClosed, setShowClosed] = useState(false);
    const [showRemoved, setShowRemoved] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [banks, setBanks] = useState([]);
    const [locations, setLocations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ customer_ledger_id: "", customer_name: "", customer_gstin: "", is_b2b: false, amount: "", date: new Date().toISOString().slice(0, 10), bank_ledger_id: "", note: "", dept: "", location: "" });

    // Live sheet state
    const [sheetCfg, setSheetCfg] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetForm, setSheetForm] = useState({ url: "", bank_ledger_id: "", default_dept: "", default_location: "" });
    const [syncing, setSyncing] = useState(false);
    const [lastSync, setLastSync] = useState(null);

    const load = async () => {
        const [a, c, all, locs, deps, sheet, ind] = await Promise.all([
            api.get("/advances", { params: showClosed ? {} : { status: "open" }}),
            api.get("/ledgers", { params: { party_type: "customer" }}),
            api.get("/ledgers"),
            api.get("/masters/locations"), api.get("/masters/departments"),
            api.get("/advances/sheet"),
            api.get("/advances/indicative", { params: showRemoved ? { include_removed: true } : {} }),
        ]);
        setItems(a.data); setCustomers(c.data);
        setBanks(all.data.filter((l) => l.party_type === "bank" || l.party_type === "cash"));
        setLocations(locs.data); setDepartments(deps.data);
        setSheetCfg(sheet.data);
        setIndicative(ind.data);
        setSheetForm({
            url: sheet.data?.url || "",
            bank_ledger_id: sheet.data?.bank_ledger_id || "",
            default_dept: sheet.data?.default_dept || "",
            default_location: sheet.data?.default_location || "",
        });
    };
    useEffect(() => { load(); }, [showClosed, showRemoved]);

    const save = async () => {
        try {
            if (!form.amount || !form.bank_ledger_id) return toast.error("Amount and bank required");
            await api.post("/advances", { ...form, amount: Number(form.amount) });
            toast.success("Advance recorded"); setOpen(false); load();
            setForm({ customer_ledger_id: "", customer_name: "", customer_gstin: "", is_b2b: false, amount: "", date: new Date().toISOString().slice(0, 10), bank_ledger_id: "", note: "", dept: "", location: "" });
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const saveSheetCfg = async () => {
        try {
            if (!sheetForm.url) return toast.error("URL is required");
            const { data } = await api.put("/advances/sheet", sheetForm);
            setSheetCfg(data);
            toast.success("Live sheet linked");
            setSheetOpen(false);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const syncNow = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/advances/sync");
            setLastSync(data);
            const parts = [
                `${data.imported_count} added`,
                `${data.duplicates} unchanged`,
                `${data.removed_count || 0} removed`,
                `${data.skipped_count} skipped`,
            ];
            const msg = parts.join(" · ");
            const changed = data.imported_count > 0 || (data.removed_count || 0) > 0;
            changed ? toast.success(msg) : toast.info(msg);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSyncing(false); }
    };

    const isLinked = !!sheetCfg?.url;

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Customer advances" sub="Money received before invoice / dispatch. Knocked off when Vyapar invoice arrives."
                right={
                    <>
                        <label className="text-xs flex items-center gap-2"><Checkbox checked={showClosed} onCheckedChange={(v) => setShowClosed(!!v)} data-testid="show-closed" />Show knocked-off</label>
                        <Button
                            onClick={syncNow}
                            disabled={!isLinked || syncing}
                            variant="outline"
                            data-testid="sync-sheet-btn"
                        >
                            <ArrowsClockwise size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
                            {syncing ? "Syncing…" : "Sync from sheet"}
                        </Button>
                        <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" data-testid="sheet-config-btn"><GearSix size={14} className="mr-2" /> Live sheet</Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                                <DialogHeader><DialogTitle className="font-serif text-2xl">Link a live Google Sheet</DialogTitle></DialogHeader>
                                <div className="space-y-4">
                                    <div className="text-xs px-3 py-2 rounded-sm space-y-1" style={{ background: "var(--se-info-bg)", color: "var(--se-info-text)" }}>
                                        <div><b>How it works — indicative only.</b> We read the sales team&apos;s Google Sheet with columns: <b>Date, Customer Name, Phone Number, Lead Type, Sale Value (INR), Sale Closed by, Advance Collected</b>. Only rows dated <b>1&nbsp;Aug 2026 or later</b> are pulled in.</div>
                                        <div>These rows are <b>indicative</b>. The <b>real bookkeeping entry</b> for a customer advance happens automatically when the matching credit shows up on the bank statement.</div>
                                        <div>In Google Sheets: <b>File → Share → Anyone with the link → Viewer</b>. Paste that link below. We only read; nothing is written back.</div>
                                    </div>
                                    <div>
                                        <Label>Google Sheet URL (edit link OR published-web CSV URL)</Label>
                                        <Input
                                            value={sheetForm.url}
                                            onChange={(e) => setSheetForm({ ...sheetForm, url: e.target.value })}
                                            placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=0"
                                            data-testid="sheet-url"
                                        />
                                    </div>
                                    <div>
                                        <Label>Money lands in <span className="text-stone-400 font-normal">(optional — for future reconciliation)</span></Label>
                                        <Select value={sheetForm.bank_ledger_id} onValueChange={(v) => setSheetForm({ ...sheetForm, bank_ledger_id: v })}>
                                            <SelectTrigger data-testid="sheet-bank"><SelectValue placeholder="Choose Cash or Bank" /></SelectTrigger>
                                            <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label>Default department (if blank in sheet)</Label>
                                            <Select value={sheetForm.default_dept || "__none__"} onValueChange={(v) => setSheetForm({ ...sheetForm, default_dept: v === "__none__" ? "" : v })}>
                                                <SelectTrigger data-testid="sheet-default-dept"><SelectValue placeholder="—" /></SelectTrigger>
                                                <SelectContent><SelectItem value="__none__">—</SelectItem>{departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>Default location (if blank in sheet)</Label>
                                            <Select value={sheetForm.default_location || "__none__"} onValueChange={(v) => setSheetForm({ ...sheetForm, default_location: v === "__none__" ? "" : v })}>
                                                <SelectTrigger data-testid="sheet-default-loc"><SelectValue placeholder="—" /></SelectTrigger>
                                                <SelectContent><SelectItem value="__none__">—</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                                <DialogFooter><Button onClick={saveSheetCfg} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="sheet-save">Save & link sheet</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild><Button style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="add-advance-btn">+ Record advance</Button></DialogTrigger>
                            <DialogContent className="max-w-lg">
                                <DialogHeader><DialogTitle className="font-serif text-2xl">Advance received</DialogTitle></DialogHeader>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <Label>Customer (existing)</Label>
                                        <ComboSelect
                                            value={form.customer_ledger_id || ""}
                                            onValueChange={(v) => setForm({ ...form, customer_ledger_id: v === "__new__" ? "" : v })}
                                            options={[
                                                { id: "__new__", name: "+ Add new customer" },
                                                ...customers.map((c) => ({ id: c.id, name: c.name }))
                                            ]}
                                            placeholder="Choose or add"
                                            testId="adv-customer"
                                        />
                                    </div>
                                    <div><Label>New customer name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="adv-cust-name" /></div>
                                    <div><Label>GSTIN (blank for B2C)</Label><Input value={form.customer_gstin} onChange={(e) => setForm({ ...form, customer_gstin: e.target.value, is_b2b: !!e.target.value })} data-testid="adv-cust-gstin" /></div>
                                    <div><Label>Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="adv-amount" /></div>
                                    <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="adv-date" /></div>
                                    <div className="col-span-2"><Label>Received into</Label>
                                        <Select value={form.bank_ledger_id} onValueChange={(v) => setForm({ ...form, bank_ledger_id: v })}>
                                            <SelectTrigger data-testid="adv-bank"><SelectValue placeholder="Choose bank/cash" /></SelectTrigger>
                                            <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label>Dept</Label>
                                        <Select value={form.dept} onValueChange={(v) => setForm({ ...form, dept: v })}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent></Select>
                                    </div>
                                    <div><Label>Location</Label>
                                        <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select>
                                    </div>
                                    <div className="col-span-2"><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="adv-note" /></div>
                                </div>
                                <DialogFooter><Button onClick={save} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="adv-save">Record</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </>
                } />
            <div className="p-8 space-y-6">
                <SheetStatusCard cfg={sheetCfg} lastSync={lastSync} isLinked={isLinked} banks={banks} onOpen={() => setSheetOpen(true)} />

                {isLinked ? (
                    <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid="indicative-card">
                        <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                            <div>
                                <div className="font-serif text-lg">Indicative advances from sales sheet</div>
                                <div className="text-xs text-stone-500">Read-only. Real ledger entry happens automatically when the matching credit lands on the bank statement. Only dates from <b>1 Aug 2026</b> onward.</div>
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="text-xs flex items-center gap-2" data-testid="ind-show-removed-label">
                                    <Checkbox checked={showRemoved} onCheckedChange={(v) => setShowRemoved(!!v)} data-testid="ind-show-removed" />
                                    Show removed
                                </label>
                                <div className="text-xs text-stone-500">{indicative.length} row{indicative.length === 1 ? "" : "s"}</div>
                            </div>
                        </div>
                        <table className="se-table" data-testid="indicative-table">
                            <thead><tr>
                                <th>Date</th><th>Customer</th><th>Phone</th><th>Closed by</th><th>Lead</th>
                                <th className="text-right">Sale value</th><th className="text-right">Advance</th><th>Status</th>
                            </tr></thead>
                            <tbody>
                                {indicative.length === 0 ? (
                                    <tr><td colSpan="8" className="text-stone-500 py-8 text-center">Nothing pulled from the sheet yet — hit <b>Sync now</b> above.</td></tr>
                                ) : indicative.map((a) => {
                                    const isRemoved = a.status === "removed_from_sheet";
                                    return (
                                        <tr key={a.id} data-testid={`ind-${a.id}`} style={isRemoved ? { opacity: 0.55 } : undefined}>
                                            <td className="font-mono text-xs">{a.date}</td>
                                            <td style={isRemoved ? { textDecoration: "line-through" } : undefined}>{a.customer_name}</td>
                                            <td className="text-xs text-stone-500 font-mono">{a.phone || "—"}</td>
                                            <td className="text-xs">{a.sale_closed_by || "—"}</td>
                                            <td className="text-xs">{a.lead_type || "—"}</td>
                                            <td className="se-num text-stone-500">{a.sale_value != null ? `₹ ${inr(a.sale_value)}` : "—"}</td>
                                            <td className="se-num" style={{ color: isRemoved ? "#78716c" : "var(--se-forest)" }}>₹ {inr(a.advance_collected)}</td>
                                            <td>
                                                {a.status === "matched" ? (
                                                    <span className="se-chip se-chip-posted">matched</span>
                                                ) : isRemoved ? (
                                                    <span className="se-chip se-chip-draft" title={a.removed_at ? `Removed on ${a.removed_at.slice(0, 10)}` : ""}>removed</span>
                                                ) : (
                                                    <span className="se-chip se-chip-warn">expected</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : null}

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="se-table" data-testid="advances-table">
                        <thead><tr><th>Date</th><th>Customer</th><th>Note</th><th>Source</th><th className="text-right">Amount</th><th className="text-right">Remaining</th><th>Status</th></tr></thead>
                        <tbody>
                            {items.length === 0 ? <tr><td colSpan="7" className="text-stone-500 py-8 text-center">No open advances.</td></tr> :
                                items.map((a) => (
                                    <tr key={a.id}>
                                        <td>{formatDate(a.date)}</td>
                                        <td>{a.customer_name}</td>
                                        <td className="text-stone-500 text-sm">{a.note || "—"}</td>
                                        <td><span className={`se-chip ${a.source === "sheet" ? "se-chip-teal" : "se-chip-draft"}`}>{a.source === "sheet" ? "sheet" : a.source || "manual"}</span></td>
                                        <td className="se-num">₹ {inr(a.amount)}</td>
                                        <td className="se-num">₹ {inr(a.remaining)}</td>
                                        <td><span className={`se-chip ${a.status === "open" ? "se-chip-warn" : "se-chip-posted"}`}>{a.status}</span></td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

function SheetStatusCard({ cfg, lastSync, isLinked, banks, onOpen }) {
    const bankName = banks.find((b) => b.id === cfg?.bank_ledger_id)?.name;
    if (!isLinked) {
        return (
            <div className="bg-white border rounded-md p-5 relative overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid="sheet-empty">
                <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: "var(--se-teal)" }} />
                <div className="flex items-center gap-3">
                    <Link size={22} weight="duotone" style={{ color: "var(--se-teal)" }} />
                    <div className="flex-1">
                        <div className="font-serif text-lg">Link a live Google Sheet</div>
                        <div className="text-sm text-stone-500">Skip manual entry. Point us at your advances sheet and we&apos;ll pull new rows on demand.</div>
                    </div>
                    <Button onClick={onOpen} style={{ background: "var(--se-teal)", color: "#fff" }} data-testid="sheet-link-cta">Link sheet</Button>
                </div>
            </div>
        );
    }
    const last = cfg?.last_synced_at;
    const result = cfg?.last_result || lastSync;
    return (
        <div className="bg-white border rounded-md p-5 relative overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid="sheet-linked">
            <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: "var(--se-forest)" }} />
            <div className="flex items-start gap-3">
                <CheckCircle size={22} weight="duotone" style={{ color: "var(--se-forest)" }} />
                <div className="flex-1">
                    <div className="font-serif text-lg">Live sheet linked</div>
                    <div className="text-sm text-stone-500 break-all">
                        <a href={cfg.url} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: "var(--se-brand)" }}>{cfg.url}</a>
                    </div>
                    <div className="text-xs mt-2 flex flex-wrap gap-x-4 gap-y-1 text-stone-500">
                        <span>Bank: <b>{bankName || "—"}</b></span>
                        <span>Last synced: <b>{last ? formatDate(last) + " · " + last.slice(11, 16) : "never"}</b></span>
                        <span>Auto-syncs daily at <b>12:00 AM IST</b></span>
                        {result ? <span>Last run: <b>{result.imported_count} new · {result.duplicates} unchanged · {result.removed_count || 0} removed · {result.skipped_count} skipped</b></span> : null}
                    </div>
                </div>
                <Button variant="ghost" size="sm" onClick={onOpen} data-testid="sheet-edit-link"><GearSix size={14} className="mr-1" /> Edit</Button>
            </div>
        </div>
    );
}
